/**
 * SQL tarafından üretilen hata metnini parçalarına ayırır.
 *
 * Saklı yordamlar hatayı `SP100_GenerateError` üzerinden fırlatıyor ve mesaj hep aynı
 * kalıpta geliyor:
 *
 *   Konum : dbo.Sp_Rn_Sales_SaveOrder
 *   Satır : [17]
 *   Mesaj : @547@Bu kayıt başka bir tabloda kullanılıyor.
 *
 * Kullanıcıyı ilgilendiren tek satır sonuncusu; ilk ikisi hatayı arayan geliştirici
 * için. Hepsi tek blok halinde gösterilince iş mesajı teknik gürültünün içinde
 * kayboluyordu, bu yüzden ayrıştırılıp ayrı gösteriliyor.
 *
 * `@547@` gibi bir önek, SQL Server'ın kendi hata numarası (50000'in altındakiler);
 * sunucu tarafı bunu mesajın başına ekliyor. Numara ayrı tutulur — mesajın içinde
 * anlamsız bir kalabalık, ama destek için değerli.
 */

export interface ParsedSqlError {
  /** Kullanıcıya gösterilecek iş mesajı. */
  message: string;
  /** Hatanın fırlatıldığı saklı yordam / trigger. */
  procedure?: string;
  /** Hata satırı. */
  line?: string;
  /** SQL Server hata numarası (varsa). */
  code?: string;
  /** Sunucunun eklediği bağlam ("Nesne güncellenirken hata oluştu"). */
  context?: string;
  /** Kalıba uymayan kısımlar; hiçbir şey kaybolmasın diye saklanır. */
  extra?: string;
  /** Metin gerçekten bu kalıptaydı mı — değilse basit bir bildirim yeter. */
  structured: boolean;
  /** Panoya kopyalanacak tam metin. */
  raw: string;
}

/**
 * Etiketler satır başına sabit DEĞİL: sunucu kendi bağlamını mesajın başına ekliyor
 * ("Nesne güncellenirken hata oluştu: Konum : ..."). Önek yakalanıp ayrı tutuluyor,
 * yoksa iş mesajının parçası sanılıyordu.
 */
const LABELS = {
  procedure: /^(.*?)(?:Konum|Location)\s*:\s*(.+)$/i,
  line: /^.*?(?:Satır|Satir|Line)\s*:\s*(.+)$/i,
  message: /^.*?(?:Mesaj|Message)\s*:\s*([\s\S]*)$/i,
};

/** `<17>` ve `[17]` biçimlerinden çıplak satır numarası. */
function cleanLine(value: string): string {
  return value.trim().replace(/^[<[]|[>\]]$/g, '');
}

/**
 * Bilinen kurulum eksiklerini anlaşılır uyarıya çevirir; tanınmayan metin null.
 *
 * Göreli tarih filtreleri (bugün, bu ay…) `dbo.Fn_Rn_DbSys_GetSystemDateTime()` kullanıyor
 * (#467). Fonksiyonu olmayan eski bir veritabanında SQL Server "Cannot find either column
 * 'dbo' or the user-defined function…" diyor; kullanıcı bunun neden olduğunu anlamıyordu.
 */
export function explainKnownSqlError(text: string | null | undefined): string | null {
  if (!text || !/Fn_Rn_DbSys_GetSystemDateTime/i.test(text)) return null;
  if (!/cannot find|could not find|invalid object|not a recognized|bulunamadı|geçersiz nesne/i.test(text)) return null;
  return 'Bu veritabanında sistem saati fonksiyonu (dbo.Fn_Rn_DbSys_GetSystemDateTime) yok. Göreli tarih filtreleri (bugün, bu hafta, bu ay…) bu fonksiyonla çalışıyor; filtreyi kaldırın ya da veritabanını güncelleyin.';
}

export function parseSqlError(raw: string | null | undefined): ParsedSqlError {
  const text = (raw ?? '').trim();
  const known = explainKnownSqlError(text);
  if (known) return { message: known, extra: text, structured: true, raw: text };
  if (!text) return { message: '', structured: false, raw: '' };

  // Sunucu bazen mesajın başına kendi bağlamını ekliyor ("Nesne güncellenirken hata
  // oluştu: ..."); kalıp o önekten sonra başlıyor.
  // SP100_GenerateError satırları yalnız CHAR(13) ile ayırıyor (#463); CR de satır sonu.
  const lines = text.split(/\r\n|\r|\n/);
  let procedure: string | undefined;
  let context: string | undefined;
  let line: string | undefined;
  let message: string | undefined;
  const rest: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const current = lines[i]!;
    const asMessage = current.match(LABELS.message);
    if (asMessage) {
      // "Mesaj:" satırından sonrası da mesaja aittir (çok satırlı açıklamalar).
      message = [asMessage[1], ...lines.slice(i + 1)].join('\n').trim();
      break;
    }
    const asProcedure = current.match(LABELS.procedure);
    if (asProcedure) {
      const prefix = (asProcedure[1] ?? '').replace(/[:\s]+$/, '').trim();
      if (prefix) context = prefix;
      procedure = asProcedure[2]!.trim();
      continue;
    }
    const asLine = current.match(LABELS.line);
    if (asLine) {
      line = cleanLine(asLine[1]!);
      continue;
    }
    if (current.trim()) rest.push(current.trim());
  }

  const structured = !!message && (!!procedure || !!line);
  if (!structured) {
    return { message: text, structured: false, raw: text };
  }

  let code: string | undefined;
  let body = message!;
  const codeMatch = body.match(/^@(\d+)@\s*/);
  if (codeMatch) {
    code = codeMatch[1];
    body = body.slice(codeMatch[0].length);
  }

  return {
    message: body.trim(),
    context,
    procedure,
    line,
    code,
    extra: rest.length ? rest.join('\n') : undefined,
    structured: true,
    raw: text,
  };
}

/** Bir metnin SQL hata kalıbı taşıyıp taşımadığı (bildirim mi pencere mi kararı için). */
export function looksLikeSqlError(text: string | null | undefined): boolean {
  return parseSqlError(text).structured;
}

/**
 * Hata, çekirdek metadata korumasından mı geliyor?
 *
 * Trigger (`Tr_Rn_Sys_*_CoreGuard`) bu kurulumda 2391 nesnenin 1116'sını koruyor.
 * Çağıran taraf bunu tanıyıp kullanıcıya "yine de kaydedilsin mi" diye sorabilsin
 * diye ayrı bir kontrol var; mesajın kendisi birkaç farklı biçimde gelebiliyor.
 */
export function isCoreGuardError(text: string | null | undefined): boolean {
  if (!text) return false;
  return /CoreGuard|core metadata|RnCoreDeploy/i.test(text);
}
